import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";

export interface QBAccount {
  Id: string;
  Name: string;
  AccountType?: string;
  AccountSubType?: string;
  CurrentBalance?: number;
  Active?: boolean;
}

function isQBAccount(value: unknown): value is QBAccount {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as QBAccount).Id === "string" &&
    typeof (value as QBAccount).Name === "string"
  );
}

export async function fetchQBAccounts(connectorId: string, connectorType: string): Promise<QBAccount[]> {
  const actionId =
    connectorType === "pipedream:quickbooks_sandbox"
      ? "quickbooks-sandbox-search-accounts"
      : "quickbooks-search-accounts";

  const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/actions/${actionId}/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      inputs: { whereClause: "Active = true", maxResults: "200", orderClause: "Name ASC" },
      connector: { id: connectorId, type: connectorType },
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch QuickBooks accounts: ${response.statusText}`);
  }

  const data = await response.json();
  const ret = data?.data?.result?.data?.ret ?? data?.data?.result?.data ?? data?.data?.result ?? data?.data ?? data;
  const queryResponse = ret?.QueryResponse ?? ret;
  const accounts = queryResponse?.Account;

  if (Array.isArray(accounts)) {
    return accounts.filter(isQBAccount);
  }

  const walk = (obj: unknown, depth = 0): QBAccount[] | null => {
    if (depth > 5 || !obj || typeof obj !== "object") return null;
    if (Array.isArray(obj)) {
      const accounts = obj.filter(isQBAccount);
      if (accounts.length > 0) {
        return accounts;
      }
    }
    for (const value of Object.values(obj as Record<string, unknown>)) {
      const found = walk(value, depth + 1);
      if (found) return found;
    }
    return null;
  };

  return walk(data) ?? [];
}

export function resolveQuickBooksAccountLabel(
  value: string | number,
  accountsById?: Map<string, QBAccount>,
): string | null {
  const normalized = String(value).trim();
  if (!normalized) return null;

  const account = accountsById?.get(normalized);
  if (account) {
    return account.Name || normalized;
  }

  return /^\d+$/.test(normalized) ? "QuickBooks account" : normalized;
}
